import { useEffect, useMemo, useRef, useState } from 'react';

type CountdownProps = {
  endsAt: number;
  totalSeconds?: number;
  hourglass?: boolean;
  warningSound?: boolean;
  bellAtSeconds?: number;
  activityLabel?: string;
};

let sharedBellAudio: HTMLAudioElement | null = null;
let audioUnlockInstalled = false;
let audioUnlocked = false;
let bellPlayingForRound: number | null = null;

function getBellAudio(): HTMLAudioElement | null {
  if (typeof window === 'undefined') return null;

  if (!sharedBellAudio) {
    const audio = new Audio('/audio/old-telephone-ring-v145.wav?v=20260927-phonebell');
    audio.preload = 'auto';
    audio.volume = 0.95;
    sharedBellAudio = audio;
  }

  return sharedBellAudio;
}

function installAudioUnlock() {
  if (audioUnlockInstalled || typeof window === 'undefined') return;
  audioUnlockInstalled = true;

  const unlock = () => {
    if (audioUnlocked) return;

    const audio = getBellAudio();
    if (!audio) return;

    const oldVolume = audio.volume;
    audio.volume = 0.001;
    audio.currentTime = 0;

    const promise = audio.play();
    if (promise) {
      void promise
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.volume = oldVolume;
          audioUnlocked = true;
        })
        .catch(() => {
          audio.volume = oldVolume;
        });
    }
  };

  window.addEventListener('pointerdown', unlock, { passive: true });
  window.addEventListener('keydown', unlock);
  window.addEventListener('touchstart', unlock, { passive: true });
}

installAudioUnlock();

function stopRealBell(roundEndsAt?: number) {
  const audio = sharedBellAudio;
  if (!audio) return;
  if (roundEndsAt !== undefined && bellPlayingForRound !== roundEndsAt) return;

  try {
    audio.pause();
    audio.currentTime = 0;
  } catch {
    // Nothing to do if the browser has already released the media element.
  }

  bellPlayingForRound = null;
}

function playRealBell(roundEndsAt: number) {
  const audio = getBellAudio();
  if (!audio) return;

  try {
    audio.pause();
    audio.currentTime = 0;
    audio.volume = 0.95;
    bellPlayingForRound = roundEndsAt;
    const promise = audio.play();
    if (promise) {
      void promise.catch(() => {
        if (bellPlayingForRound === roundEndsAt) bellPlayingForRound = null;
      });
    }
  } catch {
    // The timer must continue even if a browser blocks audio playback.
  }
}

export default function Countdown({
  endsAt,
  totalSeconds,
  hourglass = false,
  warningSound = false,
  bellAtSeconds = 15,
  activityLabel = 'الوقت',
}: CountdownProps) {
  const [now, setNow] = useState(Date.now());
  const bellPlayedForRound = useRef<number | null>(null);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), hourglass ? 120 : 250);
    return () => window.clearInterval(id);
  }, [hourglass]);

  useEffect(() => {
    if (!warningSound) return;
    installAudioUnlock();
    getBellAudio();
  }, [warningSound]);

  const remainingMs = Math.max(0, endsAt - now);
  const seconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const label = useMemo(() => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }, [seconds]);

  const totalMs = Math.max(1000, (totalSeconds ?? Math.max(1, seconds)) * 1000);
  const remainingRatio = Math.max(0, Math.min(1, remainingMs / totalMs));
  const elapsedRatio = 1 - remainingRatio;
  const warning = seconds > 0 && seconds <= 10;
  const finalFive = seconds > 0 && seconds <= bellAtSeconds;

  useEffect(() => {
    if (!warningSound) return;
    // Browsers can throttle background/mobile timers and skip the exact second.
    // Ring once as soon as we enter the final warning window instead of requiring
    // an exact `seconds === bellAtSeconds` tick.
    if (seconds <= 0 || seconds > bellAtSeconds) return;
    if (bellPlayedForRound.current === endsAt) return;

    bellPlayedForRound.current = endsAt;
    playRealBell(endsAt);
  }, [bellAtSeconds, endsAt, seconds, warningSound]);

  // The warning bell belongs to this countdown only. Stop it immediately
  // when the timer expires, warning audio is disabled, the round changes, or
  // this countdown leaves the screen (game closed/reset/phase changed).
  useEffect(() => {
    if (!warningSound || remainingMs <= 0) {
      stopRealBell(endsAt);
    }
  }, [endsAt, remainingMs, warningSound]);

  useEffect(() => {
    return () => stopRealBell(endsAt);
  }, [endsAt]);

  if (!hourglass) {
    return (
      <div className={`countdown ${warning ? 'danger' : ''}`} aria-label={`الوقت المتبقي ${label}`}>
        <span className="countdown-dot" />
        <strong>{label}</strong>
      </div>
    );
  }

  return (
    <div
      className={`countdown drawing-countdown ${warning ? 'danger' : ''} ${finalFive ? 'final-five' : ''}`}
      aria-label={`الوقت المتبقي ${label}`}
    >
      <div className="hourglass" aria-hidden="true">
        <span className="hourglass-cap top" />
        <span className="hourglass-cap bottom" />
        <span className="hourglass-glass top">
          <i className="hourglass-sand top" style={{ height: `${remainingRatio * 100}%` }} />
        </span>
        <span className="hourglass-stream" style={{ opacity: remainingMs > 0 ? 1 : 0 }} />
        <span className="hourglass-glass bottom">
          <i className="hourglass-sand bottom" style={{ height: `${elapsedRatio * 100}%` }} />
        </span>
      </div>

      <div className="drawing-countdown-copy">
        <small>{finalFive ? `أسرع! ${activityLabel} ينتهي` : activityLabel}</small>
        <div className="timer-time-row">
          {finalFive && <span className="timer-bell" aria-hidden="true">🔔</span>}
          <strong>{label}</strong>
        </div>
      </div>
    </div>
  );
}
